import cn from 'classnames';
import { CSSProperties, ComponentProps, ReactNode } from 'react';
import { OfferIcons } from '@/components/atoms/OfferIcons';
import { PlanLabel } from '@/components/atoms/PlanLabel';
import { MarketingList } from '../MarketingList';
import Button from 'de-sxm-reactlib-2/components/atoms/Button/Button';
import { type ButtonVariants } from 'de-sxm-reactlib-2/components/atoms/Button/Button';
import Text from 'de-sxm-reactlib/components/atoms/Display/Text/Text';
import ShadowBox from 'de-sxm-reactlib/components/atoms/Display/ShadowBox/ShadowBox';
import type { ListStyles, BulletTypes } from '../MarketingList/MarketingList';
import styles from './plancard.module.scss';
import { Document } from '@contentful/rich-text-types';
import { RenderRTE } from '@/components/RenderRTE';

export interface PlanCardProps {
    borderStyle?: 'line' | 'shadow';
    promoted?: boolean;
    promotedPlanLabel?: string;
    packageTitle: string;
    costDisplayStyle?: 'dynamic' | 'flat';
    packageCost: number;
    packageCostDuration: number;
    packageCostCopy: ReactNode | ReactNode[];
    offerHeaderOverride?: Document;
    packageCostSubtext: ReactNode | ReactNode[];
    showCostSeperator?: boolean;
    offerIcons?: ImageProps[];
    packageCopy: ReactNode | ReactNode[];
    packageFeatures: ComponentProps<typeof MarketingList>['items'];
    packageFeatureBodyMarginLeft?: number;
    ctaButtonText: string;
    ctaButtonTheme: ButtonVariants;
    ctaUrl: string;
    offerHeaderTextWithNoPrice: ReactNode | ReactNode[];
    isSingleCard?: boolean;
    isMobileShort?: boolean;
    bulletType?: BulletTypes;
    customStyles?: string;
    listStyle?: ListStyles;
    offerPromotionColor?: string;
    checkmarkColor?: string;
    web2?: boolean;
}

export default function PlanCard(props: PlanCardProps): React.JSX.Element {
    const web2Styles = props.web2;
    const hasButton = props.ctaUrl && props.ctaButtonText;
    const classNames = cn(styles.packageCard, {
        [styles.promoted]: props.promoted,
        [styles.singleCard]: props.isSingleCard,
        [styles.mobileShort]: props.isMobileShort,
        [styles.web2]: web2Styles,
    });
    const styleOverrides = {
        '--card-padding': web2Styles ? '20px' : props.borderStyle === 'shadow' ? '24px' : '32px',
        '--list-margin-bottom': '0',
    } as CSSProperties;

    const packageCardStyles = {
        '--border': props.borderStyle === 'shadow' ? 'none' : '1px solid',
    } as CSSProperties;

    return (
        <Wrapper borderStyle={props.borderStyle} customStyles={props.customStyles}>
            <div className={styles.cardWrapper} style={styleOverrides}>
                {props.promoted && props.promotedPlanLabel && (
                    <PlanLabel className={styles.floatTop} text={props.promotedPlanLabel} />
                )}
                <div className={classNames} style={packageCardStyles}>
                    <div className={styles.cardContent}>
                        <div className={styles.cardContentLeft}>
                            <div
                                className={cn(styles.packageContentTop, {
                                    [styles.seperator]: !web2Styles && props.showCostSeperator,
                                    [styles.titleSpacing]: web2Styles,
                                })}
                            >
                                {props.packageTitle && (
                                    <Text tag="h3" variant="h6" dataCy="molecules-plan-card-package-title">
                                        {props.packageTitle}
                                    </Text>
                                )}

                                {typeof props.packageCost === 'number' &&
                                    typeof props.packageCostDuration === 'number' && (
                                        <PlanCost
                                            costDisplayStyle={props.costDisplayStyle}
                                            packageCost={props.packageCost}
                                            offerHeaderTextWithNoPrice={props.offerHeaderTextWithNoPrice}
                                            packageCostDuration={props.packageCostDuration}
                                            offerPromotionColor={props.offerPromotionColor}
                                            offerHeaderOverride={props.offerHeaderOverride}
                                        />
                                    )}

                                {props.packageCostCopy && (
                                    <div className="no-margin small-copy text-color-gray">{props.packageCostCopy}</div>
                                )}

                                {props.packageCostSubtext && (
                                    <p className={styles.packageCostSubtext}>{props.packageCostSubtext}</p>
                                )}
                            </div>

                            {props.offerIcons && (
                                <div className={styles.offerIcons}>
                                    <OfferIcons icons={props.offerIcons} desktopWhiteFilter={false} />
                                </div>
                            )}
                        </div>

                        <div className={styles.cardContentRight}>
                            {props.packageCopy && !web2Styles && (
                                <p className={styles.packageCopy} data-cy="molecules-plan-card-package-copy">
                                    {props.packageCopy}
                                </p>
                            )}
                            {props.packageFeatures?.length && (
                                <MarketingList
                                    items={props.packageFeatures}
                                    visibleItems={props.packageFeatures.length}
                                    bulletType={props.bulletType}
                                    listStyle={props.listStyle ? props.listStyle : 'small'}
                                    checkmarkColor={props.checkmarkColor}
                                    className={cn({
                                        [styles.planCardMarketingList]: props.bulletType === 'icon',
                                        [styles.planCardCheckmarkBullet]: props.bulletType === 'checkmark',
                                    })}
                                />
                            )}
                        </div>
                    </div>

                    {hasButton && (
                        <Button
                            href={props.ctaUrl}
                            fitting="fill-container"
                            variant={props.ctaButtonTheme}
                            type="button"
                            className={styles.ctaButton}
                            dataCy="molecules-plan-card-cta-button"
                            tertiaryNoPadding={props.ctaButtonTheme === 'tertiary'}
                        >
                            {props.ctaButtonText}
                        </Button>
                    )}
                </div>
            </div>
        </Wrapper>
    );
}

interface WrapperProps {
    children: ReactNode;
    borderStyle: PlanCardProps['borderStyle'];
    customStyles?: string;
}

function Wrapper({ children, borderStyle, customStyles }: WrapperProps) {
    if (borderStyle === 'shadow' && !customStyles) {
        return <ShadowBox targetClass={styles.wrapper}>{children}</ShadowBox>;
    }
    if (customStyles) {
        return <div className={customStyles}>{children}</div>;
    }

    return <div className={styles.wrapper}>{children}</div>;
}

function PlanCost({
    costDisplayStyle = 'dynamic',
    packageCost,
    offerHeaderTextWithNoPrice,
    packageCostDuration,
    offerPromotionColor,
    offerHeaderOverride,
}: {
    costDisplayStyle: PlanCardProps['costDisplayStyle'];
    packageCost: PlanCardProps['packageCost'];
    offerHeaderTextWithNoPrice: PlanCardProps['offerHeaderTextWithNoPrice'];
    packageCostDuration: PlanCardProps['packageCostDuration'];
    offerPromotionColor: PlanCardProps['offerPromotionColor'];
    offerHeaderOverride: PlanCardProps['offerHeaderOverride'];
}) {
    return (
        <div className={styles.packageCostContainer}>
            <div>
                {costDisplayStyle === 'dynamic' ? (
                    <>
                        <div>
                            <Text
                                tag="span"
                                variant="h2"
                                className={styles.packageCost}
                                style={{ color: offerPromotionColor || '' }}
                            >
                                {packageCost === 0 ? 'Free' : `$${packageCost}`}
                            </Text>
                            <Text tag="span" className={cn(styles.packageCostDuration, 'black-weight')}>
                                {offerHeaderTextWithNoPrice}
                            </Text>
                        </div>
                    </>
                ) : packageCost === 0 && packageCostDuration === 0 ? (
                    <Text
                        tag="span"
                        variant="h2"
                        className={`${styles.packageCost} ${styles.flat}`}
                        style={{ color: offerPromotionColor || '' }}
                    >
                        <RenderRTE contentfulDoc={offerHeaderOverride} />
                    </Text>
                ) : (
                    <Text
                        tag="span"
                        variant="h2"
                        className={`${styles.packageCost} ${styles.flat}`}
                        style={{ color: offerPromotionColor || '' }}
                    >
                        {packageCost === 0 ? 'Free' : `$${packageCost} for `}
                        {packageCostDuration} month{packageCostDuration === 1 ? ' ' : 's '}
                    </Text>
                )}
            </div>
        </div>
    );
}
